import React, { useState } from 'react';
import { Grid, Paper, TextField, MenuItem, Box, Button, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Upload from '../../common/components/Upload';

const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function formatValue(val) {
  if (val === null || val === undefined) return "-";
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val)) return val.map(v => formatValue(v)).join(", ");
  if (typeof val === "object") {
    if ("exterior" in val || "interior" in val) {
      const parts = [];
      if (val.exterior) parts.push(`exterior: ${val.exterior}`);
      if (val.interior) parts.push(`interior: ${val.interior}`);
      return parts.length ? parts.join(" / ") : JSON.stringify(val);
    }
    if ("day" in val || "time" in val) return `${val.day || ""} ${val.time || ""}`.trim() || JSON.stringify(val);
    if ("name" in val) return val.name;
    return JSON.stringify(val);
  }
  return String(val);
}

// CarDetails component
const CarDetails = ({
  car = {},
  idx = 0,
  carFields = [],
  setCarFields = () => {},
  editCarIdx = null,
  setEditCarIdx = () => {},
  handleSaveCar: propHandleSaveCar = null,
  LOCATION_OPTIONS = [
    "Rachenahalli 1","Rachenahalli 2","Rachenahalli 3","HegdeNagar 1","HegdeNagar 2",
    "Bharatiya City 1","Bharatiya City 2","Bharatiya City 3","Kannur 1","Kannur 2",
    "Kannur 3","WebCity 1","WebCity 2","Kothanur 1","Kothanur 2",
    "Kothanur- Narayanpura 1","Kothanur- Narayanpura 2","Kothanur- Narayanpura 3",
    "Byrathi 1","Hennur Main Road 1","Hennur Cross 1","Ashwath nagar 1", 'Other'
  ]
}) => {
  // local fallback for edit state so Edit button works even if parent isn't controlling
  const [localEdit, setLocalEdit] = useState(false);
  // local update helper
  const handleCarChange = (carIdx, field, value) => {
    if (typeof setCarFields !== 'function') return;
    setCarFields((cars) =>
      cars.map((c, i) => (i === carIdx ? { ...c, [field]: value } : c))
    );
  };

  const handleCarFreeTimeChange = (carIdx, freeIdx, field, value) => {
    setCarFields((cars) =>
      cars.map((c, i) => {
        if (i !== carIdx) return c;
        const carFreeTime = Array.isArray(c.carFreeTime) ? [...c.carFreeTime] : [{ day: "", from: "", to: "" }];
        carFreeTime[freeIdx] = { ...carFreeTime[freeIdx], [field]: value };
        return { ...c, carFreeTime };
      })
    );
  };

  const handleRemoveCarFreeTime = (carIdx, freeIdx) => {
    setCarFields((cars) =>
      cars.map((c, i) => {
        if (i !== carIdx) return c;
        const carFreeTime = (c.carFreeTime || []).filter((_, j) => j !== freeIdx);
        return { ...c, carFreeTime: carFreeTime.length ? carFreeTime : [{ day: "", from: "", to: "" }] };
      })
    );
  };

  const handleAddCarFreeTime = (carIdx) => {
    setCarFields((cars) =>
      cars.map((c, i) => {
        if (i !== carIdx) return c;
        const carFreeTime = Array.isArray(c.carFreeTime) ? [...c.carFreeTime] : [];
        carFreeTime.push({ day: "", from: "", to: "" });
        return { ...c, carFreeTime };
      })
    );
  };

  // Save handler: prefer prop, else fallback to inline API call
  const handleSave = async (carIdx) => {
    if (typeof propHandleSaveCar === 'function') {
      await propHandleSaveCar(carIdx);
      // clear both parent and local edit state after prop save
      try { setEditCarIdx(null); } catch (e) {}
      setLocalEdit(false);
      return;
    }
    const c = carFields[carIdx];
    if (!c) return;
    const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
    try {
      // If location is 'Other', save otherLocation as location
      const carToSave = { ...c };
      if (carToSave.location === 'Other' && carToSave.otherLocation) {
        carToSave.location = carToSave.otherLocation;
        delete carToSave.otherLocation;
      }
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/cars/update/${carToSave._id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(carToSave),
      });
      const data = await res.json();
      setCarFields((cars) => cars.map((x, i) => (i === carIdx ? { ...data } : x)));
      // clear both parent and local edit state
      try { setEditCarIdx(null); } catch (e) {}
      setLocalEdit(false);
    } catch (err) {
      console.error(err);
      alert('Failed to save car');
    }
  };

  // --- new helper: convert 24h "HH:MM" to 12h parts ---
  const split24To12 = (time24) => {
    if (!time24) return { hour: '12', minute: '00', ampm: 'AM' };
    const parts = String(time24).split(':');
    if (parts.length < 2) return { hour: '12', minute: '00', ampm: 'AM' };
    let hh = parseInt(parts[0], 10);
    const mm = parts[1].slice(0,2).padStart(2, '0');
    let ampm = 'AM';
    if (hh === 0) { hh = 12; ampm = 'AM'; }
    else if (hh === 12) { ampm = 'PM'; }
    else if (hh > 12) { hh = hh - 12; ampm = 'PM'; }
    return { hour: String(hh), minute: mm, ampm };
  };

  // Render — constrain to max 33% on md, 50% on sm, full on xs
  const isEditing = (editCarIdx === idx) || localEdit;
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '50%', md: '33%' },
        flex: '0 0 auto',
      }}
    >
      {isEditing ? (
        <Paper sx={{ mb: 2, p: 2, height: "100%", width: '100%', boxSizing: 'border-box'}} variant="outlined">
          <Typography variant="h6" gutterBottom>
              Car Details
          </Typography>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <TextField label="Brand" value={car.model || ""} onChange={(e) => handleCarChange(idx, "model", e.target.value)} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Name" value={car.name || ""} onChange={(e) => handleCarChange(idx, "name", e.target.value)} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Reg No" value={car.regNo || ""} onChange={(e) => handleCarChange(idx, "regNo", e.target.value)} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Type" value={car.type || ""} onChange={(e) => handleCarChange(idx, "type", e.target.value)} fullWidth required>
                <MenuItem value="sedan">Sedan</MenuItem>
                <MenuItem value="hatchback">Hatchback</MenuItem>
                <MenuItem value="compact suv">Compact SUV</MenuItem>
                <MenuItem value="suv">SUV</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Street"
                value={car.location || ""}
                onChange={(e) => handleCarChange(idx, "location", e.target.value)}
                fullWidth
              >
                {LOCATION_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Grid>
            {car.location === 'Other' && (
              <Grid item xs={12}>
                <TextField
                  label="Other Street"
                  value={car.otherLocation || ""}
                  onChange={e => handleCarChange(idx, "otherLocation", e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField label="Color" value={car.color || ""} onChange={(e) => handleCarChange(idx, "color", e.target.value)} fullWidth />
            </Grid>

            <Grid item xs={12}>
              <TextField label="Google Map URL / GPS" value={Array.isArray(car.gps) ? `https://www.google.com/maps?q=${car.gps[0]},${car.gps[1]}` : (car.gps || "")} onChange={(e) => handleCarChange(idx, "gps", e.target.value)} fullWidth />
            </Grid>

            {/* Image upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Car Image</Typography>
              <Upload
                imageUrl={car.imageUrl || ""}
                onUploadSuccess={(url) => handleCarChange(idx, 'imageUrl', url)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField label="Notes" value={car.notes || ""} onChange={(e) => handleCarChange(idx, "notes", e.target.value)} fullWidth multiline minRows={2} />
            </Grid>

            {/* Free time editor */}
            <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Free Time</Typography>
                {(car.carFreeTime?.length ? car.carFreeTime : [{ day: '', from: '', to: '' }]).map((ft, freeIdx) => (
                  <Box key={freeIdx} sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                    <TextField
                      select
                      label="Day"
                      value={ft.day || ''}
                      onChange={e => handleCarFreeTimeChange(idx, freeIdx, 'day', e.target.value)}
                      size="small"
                      sx={{ minWidth: { xs: '48%', sm: 120 }, width: { xs: '48%', sm: 'auto' } }}
                    >
                      {WEEK_DAYS.map(day => (
                        <MenuItem key={day} value={day}>{day}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="From"
                      type="time"
                      value={ft.from || ''}
                      onChange={e => handleCarFreeTimeChange(idx, freeIdx, 'from', e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: { xs: '48%', sm: 120 }, width: { xs: '48%', sm: 'auto' } }}
                    />
                    <TextField
                      label="To"
                      type="time"
                      value={ft.to || ''}
                      onChange={e => handleCarFreeTimeChange(idx, freeIdx, 'to', e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: { xs: '48%', sm: 120 }, width: { xs: '48%', sm: 'auto' } }}
                    />
                    <IconButton
                      onClick={() => handleRemoveCarFreeTime(idx, freeIdx)}
                      disabled={(car.carFreeTime || []).length === 1}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    {freeIdx === (car.carFreeTime?.length || 1) - 1 && (
                      <IconButton
                        onClick={() => handleAddCarFreeTime(idx)}
                        size="small"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
            </Grid>
            {/* </Grid> */}

            <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {/* <Button color="error" startIcon={<DeleteIcon />} onClick={() => handleRemoveCar(idx)}>Remove</Button> */}
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => { try { setEditCarIdx(null); } catch(e){} setLocalEdit(false); }}>Cancel</Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave(idx)}>Save</Button>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <Paper sx={{ mb: 2, p: 2, height: "100%", width: '100%', boxSizing: 'border-box' }} variant="outlined">
          <Typography variant="h6" gutterBottom>
              Car Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">Brand</Typography>
              <Typography variant="body2">{formatValue(car.model)}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">Name</Typography>
              <Typography variant="body2">{formatValue(car.name)}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">Reg No</Typography>
              <Typography variant="body2">{formatValue(car.regNo)}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">Type</Typography>
              <Typography variant="body2">{formatValue(car.type)}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">Street</Typography>
              <Typography variant="body2">
                {formatValue(car.location)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">Color</Typography>
              <Typography variant="body2">{formatValue(car.color)}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Typography variant="body2" color="textSecondary">Notes</Typography>
              <Typography variant="body2" sx={{ textAlign: "right" }}>{formatValue(car.notes)}</Typography>
            </Box>
            {/* Free Time shown as single-row value (entries separated by " | ") */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Typography variant="body2" color="textSecondary">Free Time</Typography>
              <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "50%" }}>
                {Array.isArray(car.carFreeTime) && car.carFreeTime.length > 0
                  ? car.carFreeTime.map((ft) => {
                      const from = split24To12(ft.from);
                      const to = split24To12(ft.to);
                      const fromStr = ft.from ? `${from.hour}:${from.minute} ${from.ampm}` : "-";
                      const toStr = ft.to ? `${to.hour}:${to.minute} ${to.ampm}` : "-";
                      return `${ft.day || '-'} ${fromStr} - ${toStr}`;
                    }).join(" | ")
                  : "-"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Typography variant="body2" color="textSecondary">Google Maps</Typography>
              <Typography variant="body2" sx={{ textAlign: "right" }}>
                {Array.isArray(car.gps) && car.gps.length === 2
                  ? <a href={`https://www.google.com/maps?q=${car.gps[0]},${car.gps[1]}`} target="_blank" rel="noopener noreferrer">{`[${car.gps[0]}, ${car.gps[1]}]`}</a>
                  : "-"}
              </Typography>
            </Box>

            {car.imageUrl && (
              <Box>
                <img src={car.imageUrl} alt="Car" style={{ width: "100%", maxHeight: 230, objectFit: "cover", borderRadius: 8 }} />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" startIcon={<EditIcon />} onClick={() => { try { setEditCarIdx(idx); } catch(e){} setLocalEdit(true); }}>Edit</Button>
              {/* <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleRemoveCar(idx)}>Remove</Button> */}
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default CarDetails;